"use client";
import Link from "next/link";
import { Copy, Check, X } from "lucide-react";
import { useState } from "react";

interface ExistingEmailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  email: string;
}

export function ExistingEmailModal({
  open,
  onOpenChange,
  email,
}: ExistingEmailModalProps) {
  const [copied, setCopied] = useState(false);

  const handleCopyEmail = () => {
    navigator.clipboard.writeText(email);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-[#111]/50 flex items-center justify-center p-4">
      {/* Modal Container */}
      <div className="bg-[#FFF] rounded-lg shadow-lg max-w-md w-full">
        {/* Header */}
        <div className="border-b border-gray-200 p-6 flex items-start justify-between">
          <div>
            <h2 className="text-2xl font-bold">Account Already Exists</h2>
            <p className="text-gray-600 mt-2 text-base">
              An account with this email address already exists. Kindly login
              using the login button below or reset your password if you've
              forgotten it.
            </p>
          </div>
          <button
            onClick={() => onOpenChange(false)}
            className="text-gray-400 hover:text-gray-600 flex-shrink-0"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Email Display */}
        <div className="px-6 py-4 bg-[#f9fafb] mx-6 rounded-lg flex flex-col md:flex-row items-center justify-between gap-2 my-4">
          <span className="text-sm font-medium text-gray-700 break-all">
            {email}
          </span>
          <button
            onClick={handleCopyEmail}
            className="flex  items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded hover:bg-gray-50 whitespace-nowrap text-sm"
            title="Copy email"
          >
            {copied ? (
              <>
                <Check className="w-4 h-4 text-[#0ccb12]" />
                <span className="text-xs text-[#108514] font-medium">
                  Copied
                </span>
              </>
            ) : (
              <>
                <Copy className="w-4 h-4 text-[#b5b8b5]" />
                <span className="text-xs text-[#121212] font-medium">Copy</span>
              </>
            )}
          </button>
        </div>

        {/* Footer Actions */}
        <div className="flex flex-col md:flex-row gap-3 p-6 border-t border-gray-200">
          <Link href="/sign-in" className="flex-1">
            <button className="w-full px-4 py-2 border border-[#131313]  text-[#111] rounded-lg font-medium ">
              Login
            </button>
          </Link>
          <Link href="/forgot-password" className="flex-1">
            <button className="w-full px-4 py-2 bg-primary text-[#FFF] rounded-lg font-medium hover:bg-primary/90">
              Reset Password
            </button>
          </Link>
        </div>
      </div>
    </div>
  );
}
